/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/*
 * Maven: pure helpers that read progress from run-graph events and split an answer into its parts.
 * No React here, so it is easy to test.
 */

import { STREAMING } from '../../../studio/constants/StudioTextConstants';
import { REQUEST_ID_HEADER } from '../../../../common/constants';

const FENCE = /```([\w-]*)[^\S\n]*\n?([\s\S]*?)```/g;
// The model sometimes writes a chart or table as ```json or a bare ```. Those are read by their shape.
const SHAPE_FENCES = ['', 'json'];
const REQUEST_ID = /request:\s*([0-9a-f]{8}-[0-9a-f-]{27,})/gi;

// A chart or table the model wrote outside a ```chart / ```table block: inline `{...}` or a line of its own.
const LOOSE_JSON = /`(\{[^`\n]*\})`|^[^\S\n]*(\{"(?:type|columns|x|series)"[^\n]*\})[^\S\n]*$/gm;
// Headings the model writes above its SQL blocks ("SQL I used:"). The SQL blocks are taken out and not shown.
// A heading with an empty value ("SQL: none") is dropped too: with no SQL there is nothing to show.
const SQL_HEADING =
  /^[\s*_]*(here'?s )?(the )?SQL( I)?( run| ran| used| proof| query| queries)?:?[\s*_]*((none|null|n\/a)\.?[\s*_]*)?$/gim;
// An empty slot the model filled in literally ("None") instead of leaving it out.
const EMPTY_LINE = /^[\s*_-]*(none|null|n\/a)\.?[\s*_]*$/gim;

const parseJson = (text) => {
  const body = String(text || '').trim();
  try {
    return JSON.parse(body);
  } catch {
    try {
      // Models sometimes leave a trailing comma; that is the only repair made.
      return JSON.parse(body.replace(/,\s*([\]}])/g, '$1'));
    } catch {
      return null;
    }
  }
};

const toNumber = (value) => {
  const n = typeof value === 'number' ? value : Number(String(value ?? '').replace(/[%,\s]/g, ''));
  return value === null || value === '' || !Number.isFinite(n) ? null : n;
};

const looksLikeChart = (c) => c && typeof c === 'object' && Array.isArray(c.x || c.labels) && Array.isArray(c.series);

/** A chart the panel can draw: labels as text and numbers as numbers. Anything else returns null. */
const readChart = (c) => {
  if (!looksLikeChart(c)) return null;
  const x = (c.x || c.labels).map(String);
  const series = c.series
    .filter((s) => s && Array.isArray(s.data || s.values))
    .map((s, i) => ({ name: String(s.name || `Series ${i + 1}`), data: (s.data || s.values).map(toNumber) }));
  const fits = series.length > 0 && series.every((s) => s.data.length === x.length && s.data.some((n) => n !== null));
  if (!x.length || !fits) return null;
  const yMax = toNumber(c.yMax);
  return {
    type: c.type === 'line' ? 'line' : 'bar',
    title: c.title ? String(c.title) : '',
    x,
    series,
    yMax: yMax !== null && yMax > 0 ? yMax : null
  };
};

/** The same chart data as a table, for a chart that cannot be drawn. */
const chartToTable = (c) => {
  if (!looksLikeChart(c)) return null;
  const series = c.series.filter((s) => s && Array.isArray(s.data || s.values));
  if (!series.length) return null;
  return {
    columns: ['', ...series.map((s, i) => String(s.name || `Series ${i + 1}`))],
    rows: (c.x || c.labels).map((label, row) => [String(label), ...series.map((s) => (s.data || s.values)[row] ?? '')])
  };
};

const readTable = (t) =>
  t && Array.isArray(t.columns) && Array.isArray(t.rows) && t.rows.every(Array.isArray) ? t : null;

/**
 * Splits the answer into markdown text, at most one chart and one table, the SQL blocks and the proof ids.
 * A chart that cannot be drawn is shown as a table instead. Chart or table JSON is never shown as raw text:
 * if it cannot be read at all it is dropped and the text is kept.
 * @param {string} answer
 */
export const parseAnswer = (fullAnswer = '', { partial = false } = {}) => {
  // While the answer is still arriving, hide a chart / table / SQL block that has started but not
  // finished, so half-written JSON never flashes on screen. It appears once its closing ``` arrives.
  const fences = (fullAnswer.match(/```/g) || []).length;
  const answer = partial && fences % 2 === 1 ? fullAnswer.slice(0, fullAnswer.lastIndexOf('```')) : fullAnswer;
  const result = { text: '', chart: null, table: null, sql: [], requestIds: [] };
  // Keeps the first chart and the first table. Returns false when the JSON is neither.
  const takeVisual = (json) => {
    const chart = readChart(json);
    const table = chart ? null : readTable(json) || chartToTable(json);
    if (chart && !result.chart) result.chart = chart;
    if (table && !result.table) result.table = table;
    return Boolean(chart || table);
  };
  const looksLikeVisualText = (body) => /"(series|columns)"\s*:/.test(body);
  result.text = answer
    .replace(FENCE, (match, lang, body) => {
      const kind = lang.toLowerCase();
      if (kind === 'sql') {
        result.sql.push(body.trim());
        return '';
      }
      if (kind === 'chart' || kind === 'table') {
        takeVisual(parseJson(body));
        return '';
      }
      if (SHAPE_FENCES.includes(kind) && (takeVisual(parseJson(body)) || looksLikeVisualText(body))) return '';
      return match;
    })
    .replace(LOOSE_JSON, (match, inline, line) => (takeVisual(parseJson(inline || line)) ? '' : match))
    .replace(SQL_HEADING, '')
    .replace(EMPTY_LINE, '')
    // Real proof ids are shown as chips; the "Proof: ..." text line is dropped, including notes the
    // model writes when it has none ("Proof: No request ids in this summary.").
    .replace(/^[\s*_>-]*Proof\b.*$/gim, '')
    // A note about a missing proof line ("No proof line (no request ids were returned).").
    .replace(/^[\s*_>-]*No (proof|request ids?)\b.*$/gim, '')
    // A line that only lists request ids ("request:<id>"): those ids are shown as Proof chips instead.
    .replace(/^[\s*_>-]*(request:\s*[0-9a-f-]{8,}[\s,;]*)+$/gim, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  result.requestIds = [...new Set([...answer.matchAll(REQUEST_ID)].map((m) => m[1].toLowerCase()))];
  return result;
};

/**
 * The last answered turns of this chat, for follow-up questions: the answer text only (no chart, table,
 * SQL or proof ids), cut to a short length.
 * @param {Array<{ question: string, answer?: string }>} items - the chat so far, oldest first
 * @param {{ turns: number, answerChars: number }} limits
 * @returns {Array<{ question: string, answer: string }>}
 */
export const buildHistory = (items = [], { turns, answerChars }) =>
  items
    .filter((item) => item.answer && !item.error)
    .slice(-turns)
    .map(({ question, answer }) => {
      const { text } = parseAnswer(answer);
      return { question, answer: text.length > answerChars ? `${text.slice(0, answerChars)}…` : text };
    });

/** "Tool started - run_sql" -> "run_sql". Other status text returns null. */
const toolName = (content) => {
  const match = /^Tool started - (.+)$/.exec(String(content || '').trim());
  return match ? match[1] : null;
};

/**
 * What Maven's panel needs from one run-graph event, on top of the answer text that the shared
 * parseRunGraphStreamToText (the Studio stream reader) already collects: a progress step or an error.
 * @param {object} payload - one parsed stream event
 * @returns {{ step?: string, error?: string } | null}
 */
export const readStreamPayload = (payload) => {
  const { streamStatus, type, content } = payload || {};
  if (streamStatus === 'error') return { error: typeof content === 'string' ? content : 'The graph failed.' };
  if (streamStatus !== STREAMING) return null;
  if (type === 'AGENT_THINKING' && content?.agent) {
    // Only the agent name: the thinking messages ("Agent is determining…") are noise in the progress line.
    return { step: content.agent };
  }
  const tool = !type ? toolName(content) : null;
  return tool ? { step: tool } : null;
};

// What Maven is doing, in plain words, for each agent or tool step in the stream.
const STEP_LABELS = {
  Maven: 'thinking it through',
  DataAnalyst: 'looking at the data',
  get_catalog: 'working out which data to use',
  run_sql: 'running a query',
  get_identifier_usage: 'counting usage',
  get_feedback_summary: 'counting feedback',
  get_unanswered: 'counting unanswered questions',
  get_eval_runs: 'checking eval runs',
  get_eval_scores: 'checking eval scores',
  get_eval_criteria: 'checking eval criteria',
  get_eval_failures: 'checking eval failures',
  get_eval_examples: 'finding low-scored eval examples',
  get_eval_rubrics: 'checking eval rubrics',
  get_request: 'pulling up an example request',
  diff_graph: 'comparing graph versions'
};

/** "run_sql" -> "running a query". Unknown steps fall back to a general phrase. */
export const describeStep = (step) => STEP_LABELS[step] || 'working on it';

/**
 * The run's request id, from the run-graph response. berlin-api names each run itself and sends the id back in
 * X-Request-Id (exposed to the browser); it logs the run's tool calls under it, which is what verify reads.
 * @param {Response} response
 * @returns {string|null}
 */
export const readRequestId = (response) => response?.headers?.get?.(REQUEST_ID_HEADER) || null;

/** Adds a progress step unless it repeats the last one. */
export const addStep = (steps, step) => (step && steps[steps.length - 1] !== step ? [...steps, step] : steps);

export const VERIFY_STATUS = Object.freeze({
  VERIFIED: 'verified',
  VERIFIED_RECORD: 'verified_record',
  PARTLY_VERIFIED: 'partly_verified',
  NOT_VERIFIED: 'not_verified',
  NOTHING_TO_CHECK: 'nothing_to_check'
});

// What a proof id turned out to be when the answer was checked (MavenVerifyProvider's VERIFY_ID_KIND).
export const PROOF_ID_KIND = Object.freeze({ REQUEST: 'request', EVAL_RUN: 'eval_run' });
